import React from "react";
import { Box, chakra, Text } from "@chakra-ui/react";
import { ArrowDownIcon, DocumentArrowUpIcon, KeyIcon, PhotoIcon, } from "@heroicons/react/24/outline";
import { CheckCircleIcon, ExclamationCircleIcon, } from "@heroicons/react/24/solid";
import Balancer from "@hightouchio/react-wrap-balancer";
import { AnimatePresence, isValidMotionProp, motion } from "framer-motion";
import prettyBytes from "pretty-bytes";
import { ProgressBar } from "../progress-bar";
import { normalizeExtensions } from "./normalize-extensions";
import { useFileInput } from "./use-file-input";
const MotionBox = chakra(motion.div, {
    shouldForwardProp(prop) {
        return isValidMotionProp(prop) || prop === "children";
    },
});
const motionVariants = {
    slideInitial: {
        opacity: 0,
        y: -24,
    },
    slide: {
        opacity: 1,
        y: 0,
        transition: {
            delay: 0.2,
        },
    },
    scaleInitial: {
        opacity: 0,
        scale: 0,
    },
    scale: {
        opacity: 1,
        scale: 1.2,
        transition: {
            delay: 0.2,
        },
    },
    fadeInitial: {
        opacity: 0,
    },
    fade: {
        opacity: 1,
        transition: {
            delay: 0.2,
        },
    },
    exit: {
        opacity: 0,
        y: 16,
        transition: {
            type: "spring",
            damping: 30,
            stiffness: 500,
        },
    },
};
const tenMegabytes = 1000 * 1000 * 10;
const getFileType = (extensions) => {
    if (!Array.isArray(extensions) || extensions.length === 0) {
        return "generic";
    }
    const isImage = extensions.every((extension) => {
        return ["png", "jpg", "jpeg", "gif"].includes(extension);
    });
    if (isImage) {
        return "image";
    }
    const isCredentials = extensions.every((extension) => {
        return ["p8", "pem", "pub", "crt", "cert", "cer"].includes(extension);
    });
    if (isCredentials) {
        return "credentials";
    }
    return "generic";
};
const iconByFileType = {
    generic: DocumentArrowUpIcon,
    image: PhotoIcon,
    credentials: KeyIcon,
};
export function FileInput({ isDisabled = false, accept, maxSize = tenMegabytes, onUpload, }) {
    const extensions = normalizeExtensions(accept);
    const { state, fileName, errorMessage, isMouseOver, uploadProgress, getRootProps, getInputProps, } = useFileInput({
        isDisabled,
        accept: extensions,
        maxSize,
        onUpload,
    });
    const fileType = getFileType(extensions);
    const icon = iconByFileType[fileType];
    let backgroundColor = "base.lightBackground";
    let textColor;
    let borderColor = "base.border";
    let borderStyle = "dashed";
    if (state === "active" || isMouseOver) {
        backgroundColor = "white";
    }
    if (state === "success") {
        backgroundColor = "success.background";
        textColor = "text.primary";
        borderColor = "success.border";
        borderStyle = "solid";
    }
    if (state === "error") {
        backgroundColor = "danger.background";
        textColor = "text.danger";
        borderColor = "danger.border";
        borderStyle = "solid";
    }
    if (isDisabled) {
        borderStyle = "solid";
    }
    return (React.createElement(Box, { borderRadius: "md", border: "1px", borderStyle: borderStyle, borderColor: borderColor, bg: backgroundColor, transition: "all 150ms ease-in-out", paddingTop: 8, paddingBottom: 2, paddingX: 6, cursor: isDisabled ? "not-allowed" : "pointer", height: 40, opacity: isDisabled ? 0.4 : undefined, ...getRootProps() },
        React.createElement("input", { ...getInputProps() }),
        React.createElement(Box, { boxSize: 10, position: "relative", mx: "auto" },
            React.createElement(AnimatePresence, { initial: false },
                state === "inactive" && (React.createElement(MotionBox, { key: "icon", initial: "slideInitial", animate: "slide", exit: "exit", variants: motionVariants, position: "absolute", inset: 0 },
                    React.createElement(Box, { as: icon, boxSize: 10, color: "gray.base" }))),
                state === "success" && (React.createElement(MotionBox, { key: "success", initial: "scaleInitial", animate: "scale", exit: "exit", variants: motionVariants, position: "absolute", inset: 0 },
                    React.createElement(Box, { as: CheckCircleIcon, boxSize: 10, color: "success.base" }))),
                state === "error" && (React.createElement(MotionBox, { key: "error", initial: "scaleInitial", animate: "scale", exit: "exit", variants: motionVariants, position: "absolute", inset: 0 },
                    React.createElement(Box, { as: ExclamationCircleIcon, boxSize: 10, color: "danger.base" }))),
                state === "active" && (React.createElement(MotionBox, { key: "arrow", initial: "slideInitial", animate: "slide", exit: "exit", variants: motionVariants, position: "absolute", inset: 0 },
                    React.createElement(motion.div, { initial: {
                            y: 0,
                        }, animate: {
                            y: -8,
                        }, transition: {
                            repeat: Number.POSITIVE_INFINITY,
                            repeatType: "reverse",
                            duration: 0.7,
                            delay: 0.5,
                        } },
                        React.createElement(Box, { as: ArrowDownIcon, boxSize: 10, color: "gray.base" })))),
                state === "uploading" && (React.createElement(MotionBox, { key: "uploading", initial: "fadeInitial", animate: "fade", exit: "exit", variants: motionVariants, position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", justifyContent: "center" },
                    React.createElement(Text, { fontSize: "lg", fontWeight: "semibold", color: "text.primary" }, "Uploading..."))))),
        state === "uploading" ? (React.createElement(Box, { mx: "auto", mt: 6, width: "sm" },
            React.createElement(ProgressBar, { value: uploadProgress }))) : (React.createElement(React.Fragment, null,
            React.createElement(Text, { mt: 4, fontSize: "md", color: textColor, textAlign: "center" },
                state === "success" && (React.createElement(Balancer, { ratio: 0.65 },
                    "File \u201C",
                    fileName,
                    "\u201D was successfully uploaded")),
                state === "error" && (React.createElement(Balancer, { ratio: 0.65 }, errorMessage)),
                !["success", "error"].includes(state) && (React.createElement(React.Fragment, null,
                    "Drag & drop your file here, or",
                    " ",
                    React.createElement(Text, { as: "button", type: "button", color: "link.default", fontWeight: "medium", pointerEvents: isDisabled ? "none" : undefined }, "browse")))),
            React.createElement(Text, { mt: 2, fontSize: "md", color: "text.secondary", textAlign: "center", opacity: state === "success" ? 0 : 1, pointerEvents: state === "success" ? "none" : undefined },
                extensions && extensions.length > 0 && (React.createElement(React.Fragment, null,
                    "Only",
                    " ",
                    React.createElement(Text, { as: "span", textTransform: "lowercase" }, extensions.join(", ")),
                    ".")),
                " ",
                prettyBytes(maxSize),
                " max.")))));
}
